import { Aufklappmenue } from './Aufklappmenue';
import { IconProjekt } from '../Icons';
import './rahmen.css';

/**
 * Projektumschalter (Auftrag Abschnitt 4).
 *
 * ═══ WARUM ES HIER MEIST KEINEN UMSCHALTER GIBT ═════════════════════════
 *
 * Der Auftrag sagt ausdrücklich: "Falls bereits mehrere Projekte technisch
 * unterstützt werden: bestehende Funktion integrieren. Falls nicht:
 * bestehenden Projektkontext korrekt darstellen, aber keine Fake-Funktion
 * bauen."
 *
 * Die Plattform führt laut Festlegung des Fachplaners vom 20.08.2026 je
 * Container GENAU EIN Projekt (siehe den Kommentar in `App.tsx`): Parallele
 * Projekte laufen in getrennten Containern, weil ein Projekt mit anderer
 * Aufgabe andere Datenstrukturen braucht. `api.projekte()` liefert deshalb
 * im Betrieb eine einelementige Liste.
 *
 * Folglich: bei einem Projekt eine ANZEIGE (Nummer, Name, ACC-Region) - kein
 * Bedienelement, das nichts zu wechseln hat. Liefert der Server doch einmal
 * mehrere, wird daraus ohne weitere Änderung ein echter Umschalter. Genau
 * eine Zeile entscheidet das, und sie fragt die Wirklichkeit, nicht eine
 * Einstellung.
 *
 * MP-6 (F324): Die Mehrprojektplattform führt mehrere Projekte je Container,
 * jedes in seiner Datenbank. `App.tsx` gibt jetzt die VOLLE Liste herein, und
 * jeder Eintrag sagt, ob er eine „eigene Projekt-DB“ oder die „Dienst-DB“ nutzt.
 */
export interface ProjectSwitcherEintrag {
  id: string;
  /** Anzeigename, üblicherweise "8000 - Munich Arena". */
  bezeichnung: string;
  /** ACC-Region, falls bekannt. Steht klein unter der Bezeichnung. */
  region?: string | null;
  /** MP-6: „eigene Projekt-DB“ bzw. „Dienst-DB“ (`datenbankText`); fehlt sie, keine Anzeige. */
  datenbank?: string | null;
}

export interface ProjectSwitcherProps {
  /** Aktuell offenes Projekt, falls eines offen ist. */
  projekt?: ProjectSwitcherEintrag | null;
  /** Alle Projekte, zwischen denen gewechselt werden kann. */
  projekte: ProjectSwitcherEintrag[];
  onWechsel?: (id: string) => void;
}

/** Nummer/Name und Region übereinander - in beiden Fällen gleich aufgebaut. */
function Beschriftung({ projekt }: { projekt: ProjectSwitcherEintrag | null | undefined }) {
  return (
    <span className="projekt-umschalter-text">
      <span className="projekt-umschalter-name">
        {projekt?.bezeichnung ?? 'Kein Projekt geöffnet'}
      </span>
      {projekt?.region && (
        <span className="projekt-umschalter-region">ACC {projekt.region}</span>
      )}
      {projekt?.datenbank && (
        <span className="projekt-umschalter-db" data-testid="projekt-db">{projekt.datenbank}</span>
      )}
    </span>
  );
}

export function ProjectSwitcher({ projekt, projekte, onWechsel }: ProjectSwitcherProps) {
  if (projekte.length <= 1) {
    return (
      // Kein `button`: Es gibt nichts zu wählen. Ein Knopf, der auf Klick
      // nichts tut, ist genau die Fake-Funktion, die der Auftrag verbietet.
      <div className="projekt-umschalter-anzeige" aria-label="Aktuelles Projekt">
        <IconProjekt size={16} />
        <Beschriftung projekt={projekt} />
      </div>
    );
  }

  return (
    <Aufklappmenue
      ausloeserBeschriftung="Projekt wechseln"
      ausloeserKlasse="projekt-umschalter"
      ausrichtung="links"
      ausloeser={(
        <>
          <IconProjekt size={16} />
          <Beschriftung projekt={projekt} />
          <span className="aufklappmenue-pfeil" aria-hidden="true">▾</span>
        </>
      )}
    >
      {projekte.map((eintrag) => (
        <button
          key={eintrag.id}
          type="button"
          role="menuitem"
          className={`aufklappmenue-punkt${eintrag.id === projekt?.id ? ' aktiv' : ''}`}
          onClick={() => onWechsel?.(eintrag.id)}
        >
          {eintrag.bezeichnung}
          {eintrag.datenbank && (
            <span className="projekt-umschalter-db">{eintrag.datenbank}</span>
          )}
          {eintrag.id === projekt?.id && (
            <span className="aufklappmenue-haken" aria-label="aktuell geöffnet">✓</span>
          )}
        </button>
      ))}
    </Aufklappmenue>
  );
}
